import { Children, useState, ReactElement, Fragment, useMemo } from 'react'
// Material
import { Menu, MenuList, CircularProgress, ListItemIcon } from '@mui/material'
import { styled } from '@mui/material/styles'

const StyledMenu = styled(Menu)`
  text-align: right;
  .MuiMenu-root {
    position: absolute;
  }
  .MuiMenu-paper {
    background: transparent;
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.05);
  }
  .MuiBackdrop-root {
    background-color: #000;
    opacity: 0.1 !important;
  }
  .MuiMenu-list {
    padding: 0;
  }
`

const StyledMenuList = styled(MenuList)`
  padding: 2px 0;
  border: none;
  outline: none;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.palette.neutral000};
`

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color: theme.palette.primary500,
  marginLeft: '12px',

  '&.MuiListItemIcon-root': {
    minWidth: 'unset !important',
  },
}))

interface Props {
  children: (ReactElement | undefined)[] | ReactElement | undefined
  open: boolean
  xAxis: number
  yAxis: number
  onClose: () => unknown
  disableLoading?: boolean
  loadingItems?: boolean
  secondaryChildren?: ReactElement
  className?: string
}

export function ContextMenuList({
  open,
  children,
  xAxis,
  yAxis,
  onClose,
  disableLoading = false,
  loadingItems = false,
  secondaryChildren,
  className,
}: Props) {
  const [itemsDisabled, setItemsDisabled] = useState<boolean>(false)
  const [loadingItemIndex, setLoadingItemIndex] = useState<number>(-1)

  const actualChildren: ReactElement[] = useMemo(() => {
    const firstLevelChildrenArr = Children.toArray(children ?? [])
    const firstChild = firstLevelChildrenArr[0] as ReactElement

    const childIsFragment =
      Children.count(children) === 1 && firstChild?.type === Fragment

    if (childIsFragment) return firstChild?.props?.children
    return Array.isArray(children) ? children : [children]
  }, [children])

  return (
    <StyledMenu
      className={className}
      open={open}
      onClose={onClose}
      anchorPosition={{ top: yAxis, left: xAxis }}
      anchorReference="anchorPosition"
      disableRestoreFocus
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <StyledMenuList>
        {loadingItems
          ? children
          : actualChildren?.map?.((menuItem, menuItemIndex) => {
              const {
                onClick,
                children: menuItemChildren,
                ...menuItemProps
              } = menuItem?.props || {}

              const modifiedOnClick = () => {
                if (itemsDisabled || !onClick) return
                if (!disableLoading) {
                  setItemsDisabled(true)
                  setLoadingItemIndex(menuItemIndex)
                }
                onClick?.()
              }

              return menuItem ? (
                // TODO: CHECK THIS KEY TO BE UNIQUE
                <menuItem.type
                  //  eslint-disable-next-line react/jsx-props-no-spreading
                  {...menuItemProps}
                  key={menuItem.props.text}
                  onClick={modifiedOnClick}
                  disabled={itemsDisabled}
                >
                  <StyledListItemIcon>
                    {menuItemIndex === loadingItemIndex && !disableLoading ? (
                      <CircularProgress color="inherit" size={24} />
                    ) : (
                      menuItemChildren
                    )}
                  </StyledListItemIcon>
                </menuItem.type>
              ) : null
            })}
      </StyledMenuList>
      {secondaryChildren}
    </StyledMenu>
  )
}
